import { PlaybackBudget } from "./cardMediaPool";
import { bunnyMp4Candidates, setVideoSources } from "./videoKind";

// Helpers for a plain background <video data-src> (muted/loop/playsinline,
// no controls) — the native-<video> equivalent of Vidstack's
// load="visible"/load="play", for sources that don't need Vidstack (see
// src/js/utils/videoKind.js). markup (views/components/global/media.twig
// and friends) never puts a `src` on these directly, so nothing fetches
// until a component actually needs it.

// Swaps data-src -> src (then .load()) the first time it's needed. No-op for
// <media-player> (handles its own lazy `load` itself) or an already-loaded
// video.
export function loadBgVideo(video) {
  if (!video || video.tagName === "MEDIA-PLAYER" || video.src) return;
  const src = video.dataset.src;
  if (!src) return;
  setVideoSources(video, bunnyMp4Candidates(src, video, video.dataset.renditions) ?? [src]);
  video.load();
}

// Viewport-gated lazy-load + play/pause for a plain background video —
// mirrors the play-on-view IntersectionObserver every media.twig consumer
// already needed on top of Vidstack's own load="visible". Marks the element
// as owned so mountBgVideos() below skips it.
//
// `budget: true` hands play/pause to the PlaybackBudget (cardMediaPool.js),
// which caps how many play at once on tablet/mobile (no cap on desktop). The
// video still loads as soon as it's near the viewport.
export function initBgVideoOnView(video, { root = null, rootMargin = "20%", budget = false } = {}) {
  if (!video || video.tagName === "MEDIA-PLAYER") return () => {};
  video.dataset.bgVideoOwned = "";

  const play = () => {
    const p = video.play();
    if (p?.catch) p.catch(() => {});
  };
  const pause = () => video.src && video.pause();

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        loadBgVideo(video);
        if (budget) PlaybackBudget.want(video, { el: video, play, pause });
        else play();
      } else {
        if (budget) PlaybackBudget.drop(video);
        pause();
      }
    },
    { root, rootMargin, threshold: 0 },
  );
  observer.observe(video);

  return () => {
    observer.disconnect();
    if (budget) PlaybackBudget.drop(video);
  };
}

// Catch-all for a background <video data-src> no dedicated component claimed
// (sections with no JS of their own — page-success, page-about's title
// section, ...). Mirrors mountCardMedia's catch-all in cardMediaPool.js.
export function mountBgVideos(root = document) {
  root
    .querySelectorAll("video[data-src]:not([data-bg-video-owned])")
    .forEach((video) => initBgVideoOnView(video));
}
